import { useRef, useState } from "react";
import { api, STATUS } from "@/lib/api";
import { toast } from "sonner";
import { classifyScan, pedidoProgress } from "./pedidoMatch";
import { trocoSaidaPendente } from "./MobileTrocoSaida";

// Confirmação de Posse: antes de bater em_transito o entregador confirma que está com a mercadoria
// (dialog individual / "Confirmar Todas") ou dá aceite escaneando / digitando o nº do pedido.
// `troco.openDialogSoon(ms)` abre a confirmação do troco de saída quando o aceite completa.
export function useConferencia({ items, setItems, load, troco }) {
  const [confirmReceipt, setConfirmReceipt] = useState(null); // { entrega, bulk, count, trocoList, trocoOk }
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  // dedupe de POSTs — mesmo pedido escaneado 2x seguidas ou digitado + escaneado em paralelo
  const pendingConfirmRef = useRef(new Set());

  // Confirma UM pedido; entregas com múltiplos pedidos só viram em_transito quando TODOS forem
  // escaneados (backend decide e retorna `all_confirmed`).
  const directConfirmPedido = async (entrega, pedido) => {
    if (!entrega || !pedido) return null;
    const dedupeKey = `${entrega.id}:${pedido}`;
    if (pendingConfirmRef.current.has(dedupeKey)) return null;
    pendingConfirmRef.current.add(dedupeKey);
    setItems((arr) => arr.map((x) => {
      if (x.id !== entrega.id) return x;
      const already = new Set((x.pedidos_confirmados || []).map((p) => String(p).replace(/\D/g, "")));
      already.add(pedido);
      const nextConfirmed = [...already];
      const prog = pedidoProgress({ ...x, pedidos_confirmados: nextConfirmed });
      return { ...x, pedidos_confirmados: nextConfirmed, status: prog.all_done ? "em_transito" : x.status };
    }));
    try {
      const r = await api.post(`/minhas-entregas/${entrega.id}/confirmar-pedido`, { pedido });
      const data = r.data || {};
      if (data.all_confirmed) toast.success(`Pedido ${data.matched_pedido || pedido} — entrega em rota`);
      else toast.info(`Pedido ${data.matched_pedido || pedido} conferido (${data.confirmed.length}/${data.expected.length}) — falta ${(data.missing || []).length}`);
      window.dispatchEvent(new Event("entregas-changed"));
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar pedido");
      return null;
    } finally {
      pendingConfirmRef.current.delete(dedupeKey);
      load();
    }
  };

  // Scanner e barra de busca. `source` decide se mostra toast (search) ou só o flash no dialog (scan).
  const handleConferenciaLookup = (query, source = "search") => {
    const result = classifyScan(query, items);
    const isSearch = source === "search";
    if (result.kind === "empty") {
      if (isSearch) toast.error("Digite um número de pedido");
      return { kind: "empty" };
    }
    if (result.kind === "match") {
      directConfirmPedido(result.entrega, result.matched_pedido);  // fire-and-forget
      const prog = pedidoProgress(result.entrega);
      const doneAfter = prog.done + (prog.confirmed.includes(result.matched_pedido) ? 0 : 1);
      const allDone = doneAfter >= prog.total;
      if (isSearch && allDone && (Number(result.entrega.troco) || 0) > 0 && !result.entrega.troco_saida_confirmado) troco.openDialogSoon(250);
      return { kind: "match", pedido: result.pedido, entrega: result.entrega, done: doneAfter, total: prog.total, all_done: allDone };
    }
    if (result.kind === "pedido_ja_escaneado") {
      const prog = pedidoProgress(result.entrega);
      if (isSearch) toast.info(`Pedido #${result.pedido} já foi escaneado (${prog.done}/${prog.total})`);
      return { kind: "pedido_ja_escaneado", pedido: result.pedido, done: prog.done, total: prog.total };
    }
    if (result.kind === "already_confirmed") {
      if (isSearch) toast.info(`Pedido #${result.pedido} já foi confirmado (${STATUS[result.entrega.status] || result.entrega.status})`);
      return { kind: "already_confirmed", pedido: result.pedido };
    }
    if (isSearch) toast.error("Este pedido não foi atribuído a você");
    return { kind: "not_in_list" };
  };

  const askConfirmarRecebimento = (it) => setConfirmReceipt({ entrega: it, bulk: false, trocoList: trocoSaidaPendente(it) ? [it] : [], trocoOk: false });
  const askConfirmarTodas = () => {
    const list = items.filter((x) => x.status === "aguardando_conferencia");
    if (list.length === 0) return;
    setConfirmReceipt({ bulk: true, count: list.length, trocoList: list.filter(trocoSaidaPendente), trocoOk: false });
  };
  const toggleTrocoOk = () => setConfirmReceipt((c) => (c ? { ...c, trocoOk: !c.trocoOk } : c));

  const doConfirmarRecebimento = async () => {
    if (!confirmReceipt) return;
    const temTroco = (confirmReceipt.trocoList || []).length > 0;
    if (temTroco && !confirmReceipt.trocoOk) return toast.error("Confirme que recebeu o troco do balcão");
    setSavingReceipt(true);
    const body = temTroco ? { troco_confirmado: true } : {};
    const stamp = temTroco ? { troco_saida_confirmado: true, troco_saida_confirmado_at: new Date().toISOString() } : {};
    try {
      if (confirmReceipt.bulk) {
        const r = await api.post("/minhas-entregas/confirmar-todas-recebimento", body);
        toast.success(`${r.data.count} entrega(s) confirmada(s) — todas em rota`);
        setItems((arr) => arr.map((x) => (x.status === "aguardando_conferencia" ? { ...x, status: "em_transito", ...(x.troco > 0 ? stamp : {}) } : x)));
      } else {
        const it = confirmReceipt.entrega;
        await api.post(`/minhas-entregas/${it.id}/confirmar-recebimento`, body);
        toast.success(`Pedido ${it.numero_pedido || "sem número"} — confirmado em mãos`);
        setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, status: "em_transito", ...stamp } : x)));
      }
      window.dispatchEvent(new Event("entregas-changed"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao confirmar recebimento");
    } finally {
      setSavingReceipt(false);
      setConfirmReceipt(null);
    }
  };

  const onScannerOpenChange = (o) => {
    setScanOpen(o);
    if (!o && items.some((x) => x.status === "em_transito" && trocoSaidaPendente(x))) troco.openDialogSoon(150);
  };

  return {
    confirmReceipt, setConfirmReceipt, toggleTrocoOk, savingReceipt, doConfirmarRecebimento,
    askConfirmarRecebimento, askConfirmarTodas, scanOpen, setScanOpen, onScannerOpenChange, handleConferenciaLookup,
  };
}
